// src/components/FolderContentArea/DocumentPreviewPanel.jsx
//
// A right-side overlay panel that slides in over the table content.
// Uses position:absolute so it covers the table without affecting layout.
// The parent container (folder-content-area--with-preview) must have
// position:relative and overflow:hidden to scope and clip the panel.
import React from 'react';
import { useIntl } from 'react-intl';
import { FlexGroup, Icon, Skeleton, Text, Tooltip } from '@zenqms/zui';
import { PDFtron } from '../../document/PDFTron';
import './DocumentPreviewPanel.scss';

/**
 * @param {boolean}      open          — whether the panel is visible
 * @param {Function}     onClose       — called when the user closes the panel
 * @param {string}       documentName  — shown in the panel header
 * @param {string}       url           — pre-signed S3 URL passed to PDFtron
 * @param {boolean}      isLoading     — shows skeleton while URL is being fetched
 * @param {string|null}  fileExtension — extension extracted from the URL (e.g. 'pdf', 'mp4').
 *                                       PDFtron handles both PDF and video formats natively;
 *                                       passing the correct extension ensures the right viewer
 *                                       mode is activated (PDF viewer vs video viewer).
 */
export function DocumentPreviewPanel({ open, onClose, documentName, url, isLoading, fileExtension }) {
  const { formatMessage } = useIntl();

  return (
    <>
      {/* Transparent backdrop — captures clicks outside the panel */}
      <div
        className="doc-preview-panel__backdrop"
        data-open={open ? 'true' : 'false'}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="doc-preview-panel"
        data-open={open ? 'true' : 'false'}
        role="complementary"
        aria-label={formatMessage({ id: 'documentPreviewPanel.ariaLabel', defaultMessage: 'Document preview' })}
        aria-hidden={!open}
      >
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <FlexGroup alignItems="center" justifyContent="space-between" className="doc-preview-panel__header">
          <Text as="span" weight="bold" size="md" className="doc-preview-panel__title">
            {documentName || formatMessage({ id: 'documentPreviewPanel.title', defaultMessage: 'Document Preview' })}
          </Text>
          <Tooltip
            content={formatMessage({ id: 'documentPreviewPanel.close', defaultMessage: 'Close preview' })}
            position="left center"
            on={['hover', 'focus']}
            trigger={
              <button
                type="button"
                className="doc-preview-panel__close-btn"
                onClick={onClose}
                aria-label={formatMessage({ id: 'documentPreviewPanel.close', defaultMessage: 'Close preview' })}
              >
                <Icon name="xmark" noHover />
              </button>
            }
          />
        </FlexGroup>

        {/* ── Viewer area ─────────────────────────────────────────────────── */}
        <div className="doc-preview-panel__viewer">
          {isLoading && (
            <FlexGroup direction="column" gap="sm" className="doc-preview-panel__loading">
              <Skeleton variant="rectangular" height={32} />
              <Skeleton variant="rectangular" height="100%" />
            </FlexGroup>
          )}
          {!isLoading && url && open && (
            /* Key on url so PDFtron fully remounts when a different document is selected.
               fileExtension drives PDFtron's internal routing: 'pdf' → PDF viewer,
               'mp4'/'ogg'/'webm' → video viewer. */
            <PDFtron key={url} url={url} fileExtension={fileExtension ?? 'pdf'} />
          )}
        </div>
      </div>
    </>
  );
}
